import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Flex,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  IconButton,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Link,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import { MdCheck, MdOpenInFull, MdSync } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  CosellingRecommendation,
  cosellingService,
  cosellingStripInlineMarkdownSources,
  CosellingSummary,
  CosellingResponse,
} from 'services/cosellingService';
import { stripeService } from 'services/stripeService';
import { hubspotService } from 'services/hubspotService';
import { BackendSubscription, StripeSubscription } from 'types/stripe';
import { useAuth } from 'contexts/AuthContext';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';

/** Markdown-style source refs from the API: ([label](url)) or [label](url) */
const MARKDOWN_SOURCE_REF =
  /\s*\(\[([^\]]+)\]\(([^)]+)\)\)|\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Renders prose with inline markdown links turned into external Chakra links.
 * Parenthetical wrappers around a single [label](url) are removed so sentences read cleanly.
 */
function renderTextWithSourceLinks(
  text: string,
  linkColor: string,
  linkHoverColor: string,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = new RegExp(MARKDOWN_SOURCE_REF.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(text.slice(last, m.index));
    }
    const label = m[1] !== undefined && m[1] !== '' ? m[1] : m[3];
    const href = m[2] !== undefined && m[2] !== '' ? m[2] : m[4];
    nodes.push(
      <Link
        key={`src-${k++}`}
        href={href}
        isExternal
        color={linkColor}
        fontWeight="600"
        textDecoration="underline"
        textUnderlineOffset="2px"
        _hover={{ color: linkHoverColor }}
        ml={1}
      >
        {label}
      </Link>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

// Simple text display component without legacy citation noise; markdown sources become links
const SimpleText: React.FC<{
  text: string;
  fontSize?: string;
  color?: string;
}> = ({ text, fontSize = 'sm', color = 'gray.700' }) => {
  let cleanText = text;

  cleanText = cleanText.replace(/cite[^]*?cite/g, '');
  cleanText = cleanText.replace(/cite[^]*cite/g, '');
  cleanText = cleanText.replace(/cite.*?cite/g, '');
  cleanText = cleanText.replace(/cite[^]*cite/g, '');

  cleanText = cleanText.replace(
    /citeturn\d+search\d+turn\d+search\d+turn\d+search\d+/g,
    '',
  );
  cleanText = cleanText.replace(/turn\d+search\d+/g, '');

  cleanText = cleanText.replace(/\bcite\b/g, '');
  cleanText = cleanText.replace(/cite\s*$/g, '');
  cleanText = cleanText.replace(/cite\s+/g, '');

  cleanText = cleanText.replace(/[^\x20-\x7E\n\r\t]/g, '');

  cleanText = cleanText.replace(/\s+/g, ' ').trim();
  cleanText = cleanText.replace(/[.,;]\s*$/, '');

  return (
    <Text fontSize={fontSize} color={color}>
      {renderTextWithSourceLinks(cleanText, 'blue.600', 'blue.800')}
    </Text>
  );
};

const cocorevSteps = [
  'Just started...',
  'Getting there...',
  'Nearly there...',
  'Finalizing...',
  'Finito!',
];

// Keyframes for organic moving shapes
const morphAnimation = keyframes`
  0% {
    border-radius: 50% 30% 70% 40%;
    transform: rotate(0deg) scale(1);
  }
  25% {
    border-radius: 30% 70% 40% 50%;
    transform: rotate(90deg) scale(1.1);
  }
  50% {
    border-radius: 70% 40% 50% 30%;
    transform: rotate(180deg) scale(0.9);
  }
  75% {
    border-radius: 40% 50% 30% 70%;
    transform: rotate(270deg) scale(1.05);
  }
  100% {
    border-radius: 50% 30% 70% 40%;
    transform: rotate(360deg) scale(1);
  }
`;

// const pulsingDotAnimation = keyframes`
//   0%, 100% {
//     opacity: 0.4;
//     transform: scale(1);
//   }
//   50% {
//     opacity: 1;
//     transform: scale(1.2);
//   }
// `;

export const AICustomerGeneration = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [recommendations, setRecommendations] = useState<
    CosellingRecommendation[]
  >([]);
  const [summary, setSummary] = useState<CosellingSummary | null>(null);
  const [metadata, setMetadata] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<CosellingRecommendation | null>(null);
  const [subscription, setSubscription] = useState<
    StripeSubscription | BackendSubscription | null
  >(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Track companies being added to HubSpot and those already added
  const [addingToHubSpot, setAddingToHubSpot] = useState<Set<string>>(
    new Set(),
  );
  const [addedToHubSpot, setAddedToHubSpot] = useState<Set<string>>(new Set());

  // Track page view on component mount
  useEffect(() => {
    FirebaseAnalyticsService.trackNavigation(
      'AI Customer Generation',
      '/admin/ecosystem-signals',
    );
  }, []);

  // Step progression logic
  useEffect(() => {
    if (!isLoading) return;

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < cocorevSteps.length - 1) {
          return prev + 1;
        }
        return prev; // Keep at last step until loading completes
      });
    }, 24_000); // Change step every 24 seconds

    return () => clearInterval(stepInterval);
  }, [isLoading]);

  const generateRecommendations = useCallback(async () => {
    // Track AI generation start
    FirebaseAnalyticsService.trackFeatureUsage(
      'AI Customer Generation',
      'generation_started',
    );

    setIsLoading(true);
    setError(null);
    setCurrentStep(0);

    try {
      const response: CosellingResponse =
        await cosellingService.generateRecommendationsWithProfile(token);

      if (response.success) {
        setRecommendations(response.data.prospects);
        setSummary(response.data.summary);
        setMetadata(response.metadata);

        // Track successful generation
        FirebaseAnalyticsService.trackFeatureUsage(
          'AI Customer Generation',
          'generation_completed',
          {
            recommendations_count: response.data.prospects.length,
            has_summary: !!response.data.summary,
          },
        );
      } else {
        setError('Failed to generate recommendations');
        FirebaseAnalyticsService.trackFeatureUsage(
          'AI Customer Generation',
          'generation_failed',
          {
            error_type: 'api_error',
          },
        );
      }
    } catch (err: any) {
      console.error('Error generating recommendations:', err);
      setError(
        err?.message ||
          err.response?.data?.message ||
          'Failed to generate recommendations',
      );
      FirebaseAnalyticsService.trackFeatureUsage(
        'AI Customer Generation',
        'generation_failed',
        {
          error_type: 'exception',
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch subscription information
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const fetchedSubscriptions =
          await stripeService.getCustomerSubscriptions(token);
        setSubscription(fetchedSubscriptions[0] || null);
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    };

    if (token) {
      fetchSubscription();
    }
  }, [token]);

  // Auto-load recommendations when component mounts
  useEffect(() => {
    generateRecommendations();
  }, [generateRecommendations]);

  // Helper function to check if user is on free plan
  const isOnFreePlan = () => {
    if (!subscription) return false;

    // Check if it's a backend subscription with free tier
    if ('tier' in subscription) {
      return subscription.tier === 'free';
    }

    // For Stripe subscriptions, we assume they're not free
    return false;
  };

  // Helper function to check if user has connected their HubSpot portal
  const hasConnectedPortal = () => {
    if (!subscription) return false;
    return subscription.hasConnectedPortal === true;
  };

  // Handle click on Summary Generated section for free users
  const handleSummaryClick = () => {
    if (isOnFreePlan()) {
      FirebaseAnalyticsService.trackFeatureUsage(
        'Billing',
        'paywall_cta_clicked',
        { source: 'ecosystem_signals_summary' },
      );
      navigate('/admin/settings');
    }
  };

  const getConfidenceColor = (confidencePercent: number) => {
    if (confidencePercent >= 80) return 'green.500';
    if (confidencePercent >= 60) return 'yellow.500';
    return 'red.500';
  };

  const handleViewApproach = (recommendation: CosellingRecommendation) => {
    // Track recommendation interaction
    FirebaseAnalyticsService.trackFeatureUsage(
      'AI Customer Generation',
      'view_recommendation_approach',
      {
        company_name: recommendation.companyName,
        intent_strength: recommendation.intentStrength,
        confidence_percent: recommendation.confidencePercent,
      },
    );

    setSelectedRecommendation(recommendation);
    onOpen();
  };

  const handleAddToHubSpot = async (
    recommendation: CosellingRecommendation,
  ) => {
    const companyKey = recommendation.companyName;

    // Prevent duplicate clicks
    if (addingToHubSpot.has(companyKey) || addedToHubSpot.has(companyKey)) {
      return;
    }

    setAddingToHubSpot((prev) => new Set(prev).add(companyKey));

    const cleanIndustry = cosellingStripInlineMarkdownSources(
      recommendation.industry || '',
    );

    // Track HubSpot add attempt
    FirebaseAnalyticsService.trackFeatureUsage(
      'AI Customer Generation',
      'add_to_hubspot_started',
      {
        company_name: recommendation.companyName,
        industry: cleanIndustry,
      },
    );

    try {
      // Parse location into city, state, country if possible
      const cleanLocation = cosellingStripInlineMarkdownSources(
        recommendation.location || '',
      );
      const locationParts =
        cleanLocation.split(',').map((s) => s.trim()) || [];
      const [city, state, country] =
        locationParts.length === 3
          ? locationParts
          : locationParts.length === 2
            ? [undefined, locationParts[0], locationParts[1]]
            : [undefined, undefined, locationParts[0]];

      await hubspotService.createCompany(
        {
          name: recommendation.companyName,
          domain: recommendation.domain,
          industry: cleanIndustry,
          numberOfEmployees: cosellingStripInlineMarkdownSources(
            recommendation.companySize,
          ),
          city,
          state,
          country,
          intentStrength: recommendation.intentStrength,
          confidenceScore: recommendation.confidencePercent,
          partnerSources: recommendation.partnerSources,
          dataSource: recommendation.dataSource,
          techOverlapCount: recommendation.techOverlapCount,
          intentSignalCount: recommendation.intentSignalCount,
        },
        token,
      );

      // Mark as successfully added
      setAddedToHubSpot((prev) => new Set(prev).add(companyKey));

      // Track successful add
      FirebaseAnalyticsService.trackFeatureUsage(
        'AI Customer Generation',
        'add_to_hubspot_success',
        {
          company_name: recommendation.companyName,
        },
      );

      toast({
        title: 'Company added to HubSpot',
        description: `${recommendation.companyName} has been added to your HubSpot CRM.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err: any) {
      console.error('Error adding company to HubSpot:', err);

      // Track failed add
      FirebaseAnalyticsService.trackFeatureUsage(
        'AI Customer Generation',
        'add_to_hubspot_failed',
        {
          company_name: recommendation.companyName,
          error: err.message,
        },
      );

      toast({
        title: 'Failed to add company',
        description:
          err.message || 'Could not add company to HubSpot. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAddingToHubSpot((prev) => {
        const newSet = new Set(prev);
        newSet.delete(companyKey);
        return newSet;
      });
    }
  };

  // Sort recommendations: non-placeholders first, then by intent strength, confidence, and company name
  const sortedRecommendations = recommendations.sort((a, b) => {
    // Always show non-placeholder items before placeholder items
    const aIsPlaceholder = a.isPlaceholder ? 1 : 0;
    const bIsPlaceholder = b.isPlaceholder ? 1 : 0;
    const placeholderDiff = aIsPlaceholder - bIsPlaceholder;
    if (placeholderDiff !== 0) return placeholderDiff;

    const intentDiff =
      cosellingService.intentStrengthSortRank(b.intentStrength) -
      cosellingService.intentStrengthSortRank(a.intentStrength);
    if (intentDiff !== 0) return intentDiff;

    // Confidence order: higher is better
    const confidenceDiff = b.confidencePercent - a.confidencePercent;
    if (confidenceDiff !== 0) return confidenceDiff;

    // Finally sort alphabetically by company name
    return a.companyName.localeCompare(b.companyName);
  });

  return (
    <Card p={8} borderRadius="30px" mb={8}>
      <Flex justify="space-between" align="flex-start" mb={6}>
        <VStack align="flex-start" spacing={2}>
          <HStack>
            <Text fontSize="2xl" fontWeight="700">
              Ecosystem Signals
            </Text>
            <Box
              bgGradient="linear(to-r, purple.400, pink.400)"
              color="white"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="600"
              letterSpacing="wide"
            >
              BETA
            </Box>
          </HStack>
          <Text color="gray.500" maxW="600px">
            Discover leads and insights from your ecosystem, powered by AI.
          </Text>
        </VStack>
        {metadata?.summaryGeneratedAt && (
          <Tooltip
            label={
              isOnFreePlan()
                ? 'Free plan users get one generation. Click to upgrade for more regenerations.'
                : metadata?.nextRegenerationDate
                  ? `Your next customer generation will generate on ${new Date(
                      metadata.nextRegenerationDate,
                    ).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}`
                  : 'Your next regeneration will happen next month.'
            }
            placement="left"
            hasArrow
            openDelay={500}
          >
            <VStack
              align="flex-end"
              spacing={1}
              cursor={isOnFreePlan() ? 'pointer' : 'help'}
              onClick={handleSummaryClick}
              _hover={isOnFreePlan() ? { opacity: 0.8 } : {}}
              transition="opacity 0.2s"
            >
              <Text fontSize="xs" color="gray.400" fontWeight="500">
                Summary Generated
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="600">
                {new Date(metadata.summaryGeneratedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Text>
            </VStack>
          </Tooltip>
        )}
      </Flex>

      {error && (
        <Alert status="error" mb={6} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Error generating recommendations</AlertTitle>
          <AlertDescription>
            Please refresh the page to try again.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <Flex justify="center" py={12}>
          <VStack spacing={6} align="center">
            {/* Organic Moving Shapes */}
            <Box
              position="relative"
              w="50px"
              h="50px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {/* Main morphing shape */}
              <Box
                w="50px"
                h="50px"
                bgGradient="linear(45deg, purple.400, pink.400, blue.400)"
                animation={`${morphAnimation} 8s ease-in-out infinite`}
                opacity={0.8}
              />
            </Box>

            {/* Step Display */}
            <VStack spacing={2} align="center" maxW="500px">
              <Text
                fontSize="lg"
                fontWeight="600"
                color="gray.700"
                textAlign="center"
              >
                AI Customer Generation in Progress
              </Text>

              {/* Current Step */}
              {/* <Flex align="center" gap={2}>
                <Box
                  w="8px"
                  h="8px"
                  bg="purple.400"
                  borderRadius="50%"
                  animation={`${pulsingDotAnimation} 1.5s ease-in-out infinite`}
                />
                <Text fontSize="md" fontWeight="500" color="purple.700">
                  {cocorevSteps[currentStep]}
                </Text>
              </Flex> */}
            </VStack>
          </VStack>
        </Flex>
      ) : (
        <>
          {summary && (
            <Box
              bg="green.50"
              p={4}
              borderRadius="lg"
              border="1px solid"
              borderColor="green.200"
              mb={6}
            >
              {summary.keyInsights && summary.keyInsights.length > 0 && (
                <VStack align="stretch">
                  <Text fontWeight="600" color="green.800" fontSize="sm">
                    Key Insights
                  </Text>
                  {summary.keyInsights.map((insight: string, index: number) => (
                    <Text key={index} color="green.700" fontSize="sm">
                      <Box as="span" mr={1}>
                        •
                      </Box>
                      {renderTextWithSourceLinks(
                        insight,
                        'green.800',
                        'green.900',
                      )}
                    </Text>
                  ))}
                </VStack>
              )}
            </Box>
          )}

          <Box position="relative" overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Company</Th>
                  <Th>Industry</Th>
                  <Th>Size</Th>
                  <Th>Location</Th>
                  <Th>Intent Strength</Th>
                  <Th>Confidence</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedRecommendations.map((rec, index) => (
                  <Tr
                    key={index}
                    _hover={{
                      bg: rec.isPlaceholder ? 'transparent' : 'gray.50',
                    }}
                    position="relative"
                    filter={rec.isPlaceholder ? 'blur(4px)' : 'none'}
                    opacity={rec.isPlaceholder ? 0.6 : 1}
                    pointerEvents={rec.isPlaceholder ? 'none' : 'auto'}
                    userSelect={rec.isPlaceholder ? 'none' : 'auto'}
                  >
                    <Td fontWeight="600">{rec.companyName}</Td>
                    <Td>
                      {cosellingStripInlineMarkdownSources(rec.industry)}
                    </Td>
                    <Td>
                      <Badge colorScheme="blue" variant="subtle">
                        {cosellingStripInlineMarkdownSources(rec.companySize)}
                      </Badge>
                    </Td>
                    <Td>
                      {cosellingStripInlineMarkdownSources(rec.location)}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          rec.isPlaceholder
                            ? 'gray'
                            : cosellingService.intentStrengthBadgeColorScheme(
                                rec.intentStrength,
                              )
                        }
                        variant="subtle"
                      >
                        {cosellingService.intentStrengthDisplayLabel(
                          rec.intentStrength,
                        )}
                      </Badge>
                    </Td>
                    <Td>
                      <Tooltip
                        label={rec.confidenceReason || 'No reason provided'}
                        placement="top"
                        hasArrow
                        openDelay={500}
                        isDisabled={!rec.confidenceReason}
                      >
                        <Badge
                          colorScheme={
                            rec.isPlaceholder
                              ? 'gray'
                              : getConfidenceColor(
                                    rec.confidencePercent,
                                  ).includes('green')
                                ? 'green'
                                : getConfidenceColor(
                                      rec.confidencePercent,
                                    ).includes('yellow')
                                  ? 'yellow'
                                  : 'red'
                          }
                          variant="subtle"
                          cursor={rec.confidenceReason ? 'help' : 'default'}
                        >
                          {rec.confidencePercent}%
                        </Badge>
                      </Tooltip>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View details" placement="top">
                          <IconButton
                            aria-label="View details"
                            icon={<MdOpenInFull />}
                            size="sm"
                            variant="ghost"
                            colorScheme="green"
                            onClick={() => handleViewApproach(rec)}
                          />
                        </Tooltip>
                        {!hasConnectedPortal() ? (
                          <Popover trigger="hover" placement="top">
                            <PopoverTrigger>
                              <Box as="span" display="inline-block">
                                <IconButton
                                  aria-label="Sync to HubSpot"
                                  icon={<MdSync />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="gray"
                                  opacity={0.4}
                                  cursor="not-allowed"
                                  _hover={{ opacity: 0.4 }}
                                />
                              </Box>
                            </PopoverTrigger>
                            <PopoverContent
                              bg="black"
                              color="white"
                              border="none"
                              borderRadius="md"
                              px={3}
                              py={2}
                              fontSize="sm"
                              width="auto"
                              maxW="300px"
                              boxShadow="md"
                              _focus={{ outline: 'none' }}
                            >
                              <PopoverBody p={0}>
                                Connect your HubSpot portal in{' '}
                                <Link
                                  as={RouterLink}
                                  to="/admin/settings"
                                  color="blue.300"
                                  fontWeight="semibold"
                                  _hover={{ color: 'blue.200' }}
                                >
                                  Settings
                                </Link>{' '}
                                to sync companies
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <Tooltip
                            label={
                              addedToHubSpot.has(rec.companyName)
                                ? 'Synced to HubSpot'
                                : 'Sync to HubSpot CRM'
                            }
                            placement="top"
                          >
                            <IconButton
                              aria-label="Sync to HubSpot"
                              icon={
                                addingToHubSpot.has(rec.companyName) ? (
                                  <Spinner size="sm" />
                                ) : addedToHubSpot.has(rec.companyName) ? (
                                  <MdCheck />
                                ) : (
                                  <MdSync />
                                )
                              }
                              size="sm"
                              variant="ghost"
                              colorScheme={
                                addedToHubSpot.has(rec.companyName)
                                  ? 'green'
                                  : 'orange'
                              }
                              onClick={() => handleAddToHubSpot(rec)}
                              isDisabled={
                                addingToHubSpot.has(rec.companyName) ||
                                addedToHubSpot.has(rec.companyName)
                              }
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {/* Upgrade Message for Free Tier Users - Overlaid on blurred content */}
            {metadata?.subscription?.isLimited &&
              metadata?.subscription?.hiddenCount > 0 && (
                <Box
                  position="absolute"
                  top="45%"
                  left="0"
                  right="0"
                  pb={4}
                  pt={12}
                  px={4}
                  bgGradient="linear(to-t, white, rgba(255,255,255,0.95), transparent)"
                  zIndex={10}
                >
                  <Alert
                    status="info"
                    borderRadius="md"
                    bg="purple.50"
                    borderColor="purple.200"
                    border="1px solid"
                  >
                    <AlertIcon color="purple.500" />
                    <Box flex="1">
                      <AlertTitle color="purple.800">
                        Upgrade your plan to unlock all prospects
                      </AlertTitle>
                      <AlertDescription color="purple.700">
                        Upgrade to Pro to unlock all prospects and get monthly
                        customer generation.
                      </AlertDescription>
                    </Box>
                    <Button
                      as={RouterLink}
                      to="/admin/settings"
                      colorScheme="purple"
                      size="sm"
                      ml={4}
                      flexShrink={0}
                      onClick={() => {
                        FirebaseAnalyticsService.trackFeatureUsage(
                          'Billing',
                          'paywall_cta_clicked',
                          { source: 'ecosystem_signals_upgrade_overlay' },
                        );
                      }}
                    >
                      Upgrade Now
                    </Button>
                  </Alert>
                </Box>
              )}
          </Box>
        </>
      )}

      {/* Suggested Approach Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="lg" fontWeight="700">
                Company Details
              </Text>
              {selectedRecommendation && (
                <Text fontSize="md" color="gray.600">
                  {selectedRecommendation.companyName}
                </Text>
              )}
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedRecommendation && (
              <VStack align="stretch" spacing={4}>
                <Box
                  bg="green.50"
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text fontWeight="600" color="green.800" mb={2}>
                    Ecosystem Signal
                  </Text>
                  <SimpleText
                    text={selectedRecommendation.ecosystemSignal}
                    fontSize="sm"
                    color="green.700"
                  />
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.700" mb={2}>
                    Company Details
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Domain:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {selectedRecommendation.domain}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Industry:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {cosellingStripInlineMarkdownSources(
                          selectedRecommendation.industry,
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Size:
                      </Text>
                      <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                        {cosellingStripInlineMarkdownSources(
                          selectedRecommendation.companySize,
                        )}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Location:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {cosellingStripInlineMarkdownSources(
                          selectedRecommendation.location,
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Intent Strength:
                      </Text>
                      <Badge
                        colorScheme={cosellingService.intentStrengthBadgeColorScheme(
                          selectedRecommendation.intentStrength,
                        )}
                        variant="subtle"
                        fontSize="xs"
                      >
                        {cosellingService.intentStrengthDisplayLabel(
                          selectedRecommendation.intentStrength,
                        )}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Confidence Score:
                      </Text>
                      <Tooltip
                        label={
                          selectedRecommendation.confidenceReason ||
                          'No reason provided'
                        }
                        placement="top"
                        hasArrow
                        openDelay={500}
                        isDisabled={!selectedRecommendation.confidenceReason}
                      >
                        <Badge
                          colorScheme={
                            getConfidenceColor(
                              selectedRecommendation.confidencePercent,
                            ).includes('green')
                              ? 'green'
                              : getConfidenceColor(
                                    selectedRecommendation.confidencePercent,
                                  ).includes('yellow')
                                ? 'yellow'
                                : 'red'
                          }
                          variant="subtle"
                          fontSize="xs"
                          cursor={
                            selectedRecommendation.confidenceReason
                              ? 'help'
                              : 'default'
                          }
                        >
                          {selectedRecommendation.confidencePercent}%
                        </Badge>
                      </Tooltip>
                    </HStack>
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.700" mb={2}>
                    Partner Information
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Partner Sources:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {selectedRecommendation.partnerSources}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Intent Strength:
                      </Text>
                      <Badge
                        colorScheme={cosellingService.intentStrengthBadgeColorScheme(
                          selectedRecommendation.intentStrength,
                        )}
                        variant="subtle"
                        fontSize="xs"
                      >
                        {cosellingService.intentStrengthDisplayLabel(
                          selectedRecommendation.intentStrength,
                        )}
                      </Badge>
                    </HStack>
                    <Box>
                      <Text color="gray.600" fontSize="sm" mb={2}>
                        Data Source:
                      </Text>
                      <SimpleText
                        text={selectedRecommendation.dataSource}
                        fontSize="sm"
                        color="gray.700"
                      />
                    </Box>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Tech Overlap Count:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {selectedRecommendation.techOverlapCount}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600" fontSize="sm">
                        Intent Signal Count:
                      </Text>
                      <Text fontWeight="500" fontSize="sm">
                        {selectedRecommendation.intentSignalCount}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};
